// src/components/PDF/ConvertPDF.js
import React, { useState } from 'react';
import { Upload, Download, Image, Settings } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const ConvertPDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [convertedFile, setConvertedFile] = useState(null);
  const [quality, setQuality] = useState(2);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        setConvertedFile(null);
      }
    }
  });

  const convertToJPEG = async () => {
    if (!pdfFile) {
      toast.error('Please select a PDF file to convert');
      return;
    }

    setIsLoading(true);
    try {
      const zipBlob = await pdfService.convertToJPEG(pdfFile, quality);
      setConvertedFile({
        blob: zipBlob,
        name: `${pdfFile.name.replace('.pdf', '')}_images.zip`
      });
      toast.success('PDF converted to JPEG images successfully!');
    } catch (error) {
      toast.error('Error converting PDF to JPEG');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadConverted = () => {
    requireAuth(() => {
      if (convertedFile) {
        const url = URL.createObjectURL(convertedFile.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = convertedFile.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetForm = () => {
    setPdfFile(null);
    setConvertedFile(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Convert PDF to JPEG</h2>
      
      {/* File Upload Zone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive 
            ? 'border-[#232751] bg-[#232751]/5' 
            : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-sm font-medium text-gray-600 mb-2">
          {isDragActive ? 'Drop PDF here...' : 'Click to select or drag PDF here'}
        </p>
        <p className="text-xs text-gray-500">
          Upload a PDF to convert to JPEG images
        </p>
      </div>

      {/* Quality Settings */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Settings className="w-5 h-5 text-gray-600" />
          <h3 className="font-medium text-gray-700">Conversion Settings</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label htmlFor="quality" className="block text-sm font-medium text-gray-700 mb-2">
              Image Quality: {quality}x
            </label>
            <input
              type="range"
              id="quality"
              min="1"
              max="4"
              step="0.5"
              value={quality}
              onChange={(e) => setQuality(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Low (1x)</span>
              <span>Medium (2x)</span>
              <span>High (3x)</span>
              <span>Ultra (4x)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected File */}
      {pdfFile && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Image className="w-6 h-6 text-[#232751]" />
            <div>
              <p className="font-medium text-gray-700">{pdfFile.name}</p>
              <p className="text-sm text-gray-500">
                {(pdfFile.size / 1024 / 1024).toFixed(2)} MB • Quality: {quality}x
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={convertToJPEG}
          disabled={!pdfFile || isLoading}
          className="flex-1 bg-[#232751] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#1b1f43] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Image className="w-5 h-5 mr-2" />
          Convert to JPEG
        </button>
        
        {pdfFile && (
          <button
            onClick={resetForm}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Download Section */}
      {convertedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Image className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Conversion Complete!</p>
                <p className="text-sm text-green-600">
                  Your JPEG images are ready for download as a ZIP file
                </p>
              </div>
            </div>
            <button
              onClick={downloadConverted}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download ZIP
            </button>
          </div>
        </div>
      )}
      {AuthModal}
    </div>
  );
};

export default ConvertPDF;
